import React from "react";
import { Dialog } from "@headlessui/react";
import { MdClose } from "react-icons/md";
import Button from "../ui/Button";
import Moment from "react-moment";

const RequestDetailModal = ({
  isOpen,
  onClose,
  request,
  onApprove,
  onReject,
}) => {
  if (!request) return null;

  const getEmployeeName = (req) => {
    return (
      req?.employee?.name ||
      (req?.employee?.first_name
        ? `${req.employee.first_name} ${req.employee.last_name || ""}`.trim()
        : null) ||
      req?.user?.name ||
      (req?.user?.first_name
        ? `${req.user.first_name} ${req.user.last_name || ""}`.trim()
        : null) ||
      "--"
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "--";
    return timeString.substring(0, 5);
  };

  const formatAttendanceDate = (date, month, year) => {
    return `${date}/${month}/${year}`;
  };

  const getAttendanceDateForMoment = (date, month, year) => {
    return new Date(`${year}-${month}-${date}`);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="fixed inset-0 z-[1002]"
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />

        <div className="relative bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b shrink-0">
            <Dialog.Title className="text-lg font-semibold">
              Regularization Request Details
            </Dialog.Title>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <MdClose className="w-6 h-6" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-6">
                {/* Employee Info */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">
                    Employee Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
                    <div>
                      <p className="text-xs text-gray-500">Employee Name</p>
                      <p className="font-medium">{getEmployeeName(request)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Company</p>
                      <p className="font-medium">
                        {request.company?.company_name || "--"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Attendance Date */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">
                    Attendance Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
                    <div>
                      <p className="text-xs text-gray-500">Attendance Date</p>
                      <p className="font-medium">
                        {formatAttendanceDate(
                          request.attendance_date,
                          request.attendance_month,
                          request.attendance_year
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Day</p>
                      <p className="font-medium">
                        <Moment
                          date={getAttendanceDateForMoment(
                            request.attendance_date,
                            request.attendance_month,
                            request.attendance_year
                          )}
                          format="dddd"
                        />
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Request Submitted</p>
                      <p className="font-medium">
                        {formatDate(request.created_at)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Request Status */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">
                    Request Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded">
                    <div>
                      <p className="text-xs text-gray-500">Status</p>
                      <span
                        className={`inline-block ${getStatusBadgeColor(
                          request.status
                        )} px-3 py-1 rounded-full text-xs font-medium capitalize`}
                      >
                        {request.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                {/* Attendance Status */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">
                    Attendance Status
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded">
                      <p className="text-xs text-gray-500 mb-1">Current Status</p>
                      <p className="font-semibold capitalize">
                        {request.current_status?.replace(/_/g, " ")}
                      </p>
                    </div>
                    <div className="bg-blue-50 p-4 rounded border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">
                        Requested Status
                      </p>
                      <p className="font-semibold capitalize text-blue-900">
                        {request.requested_status?.replace(/_/g, " ")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Times */}
                {(request.requested_in_time ||
                  request.requested_out_time ||
                  request.current_in_time ||
                  request.current_out_time) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-600 mb-3">
                      Work Hours
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      {/* Current/Original */}
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="text-xs text-gray-500 mb-3">Current Hours</p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Punch In</p>
                            <p className="font-mono font-medium text-sm">
                              {formatTime(request.current_in_time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Punch Out</p>
                            <p className="font-mono font-medium text-sm">
                              {formatTime(request.current_out_time)}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Requested */}
                      <div className="bg-blue-50 p-4 rounded border border-blue-200">
                        <p className="text-xs text-blue-600 font-medium mb-3">Requested Hours</p>
                        <div className="space-y-3">
                          <div>
                            <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Punch In</p>
                            <p className="font-mono font-semibold text-sm text-blue-900">
                              {formatTime(request.requested_in_time)}
                            </p>
                          </div>
                          <div>
                            <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Punch Out</p>
                            <p className="font-mono font-semibold text-sm text-blue-900">
                              {formatTime(request.requested_out_time)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Request Reason */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-600 mb-3">
                    Request Reason
                  </h3>
                  <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded">
                    {request.request_reason || "--"}
                  </p>
                </div>

                {/* Approval/Rejection Info */}
                {request.status === "approved" && (
                  <div className="bg-green-50 border border-green-200 rounded p-4">
                    <p className="text-xs text-green-700">
                      Approved by {request.approved_by_name || "Admin"} on{" "}
                      {formatDate(request.approved_at)}
                    </p>
                  </div>
                )}

                {request.status === "rejected" && (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-xs text-red-700 mb-2">
                      Rejected by {request.rejected_by_name || "Admin"} on{" "}
                      {formatDate(request.rejected_at)}
                    </p>
                    {request.reject_reason && (
                      <div className="bg-white p-2 rounded text-xs text-red-800 border border-red-200">
                        <span className="font-medium">Reason:</span>{" "}
                        {request.reject_reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Fixed at bottom */}
          {request.status === "pending" && (
            <div className="flex justify-end space-x-3 p-4 border-t shrink-0 bg-white">
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button type="button" variant="destructive" onClick={onReject}>
                Reject
              </Button>
              <Button type="button" variant="success" onClick={onApprove}>
                Approve
              </Button>
            </div>
          )}

          {request.status !== "pending" && (
            <div className="flex justify-end p-4 border-t shrink-0 bg-white">
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default RequestDetailModal;
